import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { SMASH_IMAGES } from "@/lib/assets";
import { SPORTS, EDUCATION_TOPICS } from "@/lib/content";
import { api } from "@/lib/api";
import EventCard from "@/components/EventCard";
import FightCard from "@/components/FightCard";
import NewsCard from "@/components/NewsCard";
import SectionTitle from "@/components/SectionTitle";
import ResponsibleBanner from "@/components/ResponsibleBanner";
import { Flame, CalendarClock, History, Newspaper, BookOpen, ChevronRight } from "lucide-react";

export default function SportPage() {
  const { sport } = useParams();
  const meta = SPORTS.find((s) => s.key === sport);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [updated, setUpdated] = useState(null);

  useEffect(() => {
    if (!meta) return;
    api.get("/events", { params: { sport } }).then((r) => setEvents(r.data || [])).catch(() => {});
    api.get("/news", { params: { sport } }).then((r) => setNews(r.data || [])).catch(() => {});
    api.get("/meta").then((r) => setUpdated(r.data?.last_updated)).catch(() => {});
  }, [sport, meta]);

  if (!meta) return <Navigate to="/" replace />;

  const isFight = sport === "boxing" || sport === "mma";
  const Card = isFight ? FightCard : EventCard;
  const live = events.filter((e) => e.status === "live");
  const upcoming = events.filter((e) => e.status === "upcoming");
  const finals = events.filter((e) => e.status === "final");
  const eduTopics = EDUCATION_TOPICS.slice(0, 4);

  const Grid = ({ list, empty }) =>
    list.length ? (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{list.map((e) => <Card key={e.id} event={e} />)}</div>
    ) : (
      <div className="panel p-8 text-center text-slate-500">{empty}</div>
    );

  return (
    <div>
      <div className="relative h-[280px] overflow-hidden">
        <img src={SMASH_IMAGES[sport]} alt={meta.label} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-[#0A0B0E]/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-[1500px] mx-auto px-6 pb-6">
          <div className="text-xs font-bold tracking-[0.2em] text-smasher-orange">{meta.league}</div>
          <h1 className="font-heading font-black uppercase text-4xl sm:text-6xl text-gradient-gold">{meta.label}</h1>
          {updated && <div className="text-[11px] font-mono-num text-slate-400 mt-1" data-testid="sport-last-updated">LAST UPDATED: {new Date(updated).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>}
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-10 space-y-14">
        {live.length > 0 && (
          <section><SectionTitle kicker="HAPPENING NOW" title="Live & Today" icon={Flame} /><Grid list={[...live, ...upcoming.slice(0, 3)]} empty="Nothing live right now." /></section>
        )}
        <section><SectionTitle kicker="ON THE SCHEDULE" title="Upcoming Events" icon={CalendarClock} /><Grid list={upcoming} empty="No upcoming events posted yet." /></section>
        <section><SectionTitle kicker="RECENT RESULTS" title="Final Scores" icon={History} /><Grid list={finals} empty="No recent results available." /></section>

        <section>
          <SectionTitle kicker="FROM THE SMASHER DESK" title={`Latest ${meta.label} News`} icon={Newspaper} />
          {news.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{news.map((n) => <NewsCard key={n.id} item={n} />)}</div>
          ) : (
            <div className="panel p-8 text-center text-slate-500">No news available for {meta.label} right now.</div>
          )}
        </section>

        <section>
          <SectionTitle kicker="LEARN THE GAME" title="Betting Education" icon={BookOpen} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {eduTopics.map((t) => (
              <Link key={t.slug} to={`/education/${t.slug}`} className="panel panel-hover p-5 group">
                <h3 className="font-heading font-bold text-lg text-white group-hover:text-smasher-gold">{t.title}</h3>
                <p className="text-sm text-slate-400 mt-1">{t.short}</p>
                <span className="text-xs text-smasher-gold font-bold mt-3 inline-flex items-center gap-1">LEARN <ChevronRight className="w-3.5 h-3.5" /></span>
              </Link>
            ))}
          </div>
        </section>

        <ResponsibleBanner />
      </div>
    </div>
  );
}
